// Import necessary modules and components
import * as React from "react";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import AppAppBar from "../components/AppAppBar";
import Footer from "../components/Footer";
import getLPTheme from "./getLPTheme";
import { Divider } from "@mui/material";
import EventCalendar from "../components/Calendar/EventCalendar";
import BasicSelect from "../components/BasicSelect";
import Stack from "@mui/material/Stack";
import Container from "@mui/material/Container";

// Special value for the "All" rooms option
const ALL_ROOMS_VALUE = "all_rooms";

/**
 * Rooms page component for displaying and selecting meeting rooms
 * Supports URL parameters for auto-selection and guest mode
 *
 * @returns {JSX.Element} The rendered Rooms component
 */
export default function Rooms() {
  const [mode, setMode] = useState("light");
  const LPtheme = createTheme(getLPTheme(mode));
  const [roomsList, setRoomsList] = useState([]);
  const [locationsList, setLocationsList] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [roomName, setRoomName] = useState("");
  const [roomID, setRoomID] = useState("");
  const [isAllRoomsView, setIsAllRoomsView] = useState(false);
  const [guestKey, setGuestKey] = useState("");
  const [isGuestMode, setIsGuestMode] = useState(false);
  const [urlParamsProcessed, setUrlParamsProcessed] = useState(false);
  const [pendingRoom, setPendingRoom] = useState(null);
  const prevLocationRef = useRef(selectedLocation);

  // API endpoints
  const getRoomsApi = process.env.REACT_APP_API_URL + "/api/get-rooms-list";
  const getLocationsApi = process.env.REACT_APP_API_URL + "/api/get-locations";

  // Get filtered rooms for the selected location using useMemo for performance
  const filteredRooms = useMemo(() => {
    if (!selectedLocation) return [];

    const locationStr = String(selectedLocation);
    const filtered = roomsList.filter(
      (room) => String(room.lid) === locationStr
    );

    return filtered;
  }, [selectedLocation, roomsList]);

  // Get the name of the selected location
  const selectedLocationName = useMemo(() => {
    return selectedLocation
      ? locationsList.find((location) => location.lid === selectedLocation)
          ?.name || ""
      : "";
  }, [selectedLocation, locationsList]);

  /**
   * Parse URL parameters and set initial state based on URL
   * Auto-selects location and room if provided in URL
   * Enables guest mode if guestKey is present
   */
  useEffect(() => {
    if (urlParamsProcessed) return; // Prevent multiple processing

    const urlParams = new URLSearchParams(window.location.search);
    const locationParam = urlParams.get("location");
    const roomParam = urlParams.get("room");
    const guestKeyParam = urlParams.get("guestKey");

    // Set guest mode if guestKey is present
    if (guestKeyParam) {
      setGuestKey(guestKeyParam);
      setIsGuestMode(true);
    }

    // Set location from URL parameter
    if (locationParam) {
      setSelectedLocation(locationParam);
    }

    // Store room parameter for later processing
    if (roomParam) {
      setPendingRoom(roomParam);
    }

    setUrlParamsProcessed(true);
  }, [urlParamsProcessed]);

  /**
   * Gets the JWT token from cookies
   * @returns {string|null} The JWT token or null if not found
   */
  const getAuthToken = useCallback(() => {
    const token = document.cookie
      .split("; ")
      .find((row) => row.startsWith("_auth="))
      ?.split("=")[1];
    // No error thrown here if token is not found, as it's optional for this endpoint
    return token;
  }, []);

  // Fetch rooms from the API
  useEffect(() => {
    const fetchRooms = async () => {
      try {
        const response = await axios.get(getRoomsApi);
        setRoomsList(response.data);
      } catch (error) {
        console.error("Error fetching rooms:", error);
      }
    };

    fetchRooms();
  }, [getRoomsApi]);

  // Fetch locations from the API
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const token = getAuthToken();
        const headers = {};
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }
        const response = await axios.get(getLocationsApi, {
          headers: headers,
          withCredentials: true, // Important for sending cookies if backend expects them
        });
        setLocationsList(response.data);
      } catch (error) {
        console.error("Error fetching locations:", error);
      }
    };

    fetchLocations();
  }, [getLocationsApi, getAuthToken]);

  const toggleColorMode = () => {
    setMode((prev) => (prev === "dark" ? "light" : "dark"));
  };

  // Handle room selection
  const handleRoomSelection = useCallback(
    (value) => {
      setSelectedRoom(value);

      // Check if "All Rooms" is selected
      if (value === ALL_ROOMS_VALUE) {
        setIsAllRoomsView(true);
        setRoomName(`All Rooms at ${selectedLocationName}`);
        setRoomID(value);
      } else if (value) {
        // Set room name based on selected room
        setIsAllRoomsView(false);
        const selectedRoomData = roomsList.find(
          (room) => String(room.room_id) === String(value)
        );

        if (selectedRoomData) {
          setRoomName(selectedRoomData.name);
          setRoomID(selectedRoomData.room_id);
        } else {
          console.warn("Room data not found for ID:", value);
          setRoomName("");
          setRoomID("");
        }
      } else {
        // Handle empty selection
        setRoomName("");
        setRoomID("");
        setIsAllRoomsView(false);
      }
    },
    [selectedLocationName, roomsList]
  );

  // Handle pending room selection once data is available
  useEffect(() => {
    if (pendingRoom && filteredRooms.length > 0) {
      const roomParamStr = String(pendingRoom);
      
      const roomExists = filteredRooms.some(
        (room) => String(room.room_id) === roomParamStr
      );

      if (roomExists) {
        handleRoomSelection(roomParamStr);
        setPendingRoom(null);
      }
    }
  }, [pendingRoom, filteredRooms, handleRoomSelection]);

  // Reset room selection when location changes (only if not in guest mode)
  useEffect(() => {
    // Only reset if location ACTUALLY changed
    if (prevLocationRef.current !== selectedLocation) {
      if (!isGuestMode && !pendingRoom) {
        setSelectedRoom("");
        setRoomName("");
        setRoomID("");
        setIsAllRoomsView(false);
      }
      prevLocationRef.current = selectedLocation;
    }
  }, [selectedLocation, isGuestMode, pendingRoom]);

  /**
   * Handle location selection - only allow changes if not in guest mode
   * @param {string} value - The selected location value
   */
  const handleLocationSelection = (value) => {
    if (!isGuestMode) {
      setSelectedLocation(value);
    }
  };

  /**
   * Handle room selection - only allow changes if not in guest mode
   * @param {string} value - The selected room value
   */
  const handleRoomSelectionWithGuestCheck = (value) => {
    if (!isGuestMode) {
      handleRoomSelection(value);
    }
  };

  // Debug effect to track state changes
  useEffect(() => {
    // State tracking for debugging purposes
  }, [
    selectedLocation,
    selectedRoom,
    roomName,
    roomID,
    isGuestMode,
    urlParamsProcessed,
    roomsList.length,
    locationsList.length,
  ]);

  return (
    <ThemeProvider theme={LPtheme}>
      <CssBaseline />
      <AppAppBar
        mode={mode}
        toggleColorMode={toggleColorMode}
      />
      <Box
        sx={{
          backgroundImage:
            "linear-gradient(180deg, rgba(203, 224, 15, 0.2), #fff)",
          backgroundSize: "100% 200px",
          backgroundRepeat: "no-repeat",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          pt: { xs: 14, sm: 20 },
        }}
      >
        <Divider />
        <Stack
          direction={{ xs: "column", sm: "row" }}
          textAlign='center'
          alignSelf='center'
          spacing={1.5}
          useFlexGap
          sx={{ pt: 2, width: { xs: "auto", sm: "auto" } }}
          marginBottom={5}
        >
          <BasicSelect
            label='Location'
            itemList={locationsList.map((location) => ({
              value: location.lid,
              label: location.name,
            }))}
            selectedValue={selectedLocation}
            onValueChange={handleLocationSelection}
            disabled={isGuestMode} // Disable when in guest mode
          />

          <BasicSelect
            label='Room'
            itemList={[
              // Add the "All" option when a location is selected
              ...(selectedLocation
                ? [
                    {
                      value: ALL_ROOMS_VALUE,
                      label: `All Rooms at ${selectedLocationName}`,
                    },
                  ]
                : []),
              // Add the individual rooms
              ...filteredRooms
                .sort((a, b) => a.cap - b.cap) // Sort rooms by capacity
                .map((room) => ({
                  value: room.room_id,
                  label: `${room.name} (Capacity: ${room.cap})`,
                })),
            ]}
            selectedValue={selectedRoom}
            onValueChange={handleRoomSelectionWithGuestCheck}
            disabled={isGuestMode} // Disable when in guest mode
          />
        </Stack>

        {/* Add Container to manage Calendar width */}
        <Container
          maxWidth='xl'
          sx={{ mt: 4 }}
        >
          {" "}
          {/* Adjust maxWidth and margin as needed */}
          {/* Render the calendar when a selection is made */}
          {selectedRoom && (
            <EventCalendar
              key={selectedRoom}
              roomName={roomName}
              roomid={roomID}
              isAllRoomsView={isAllRoomsView}
              locationId={selectedLocation}
              allRooms={isAllRoomsView ? filteredRooms : []}
              guestKey={guestKey} // Pass guest key to calendar if needed
            />
          )}
        </Container>
        <Footer />
      </Box>
    </ThemeProvider>
  );
}
